import { useQuery } from '@tanstack/react-query';
import http from '../../../utils/http';
import { ReportResponse } from '../types';
import { useTranslation } from 'utils/translation';
import { useShallowEqualEffect } from '../../../utils/use-shallow-effect';
import { useUiVersion } from '../../ui/UserInterfaceVersionProvider';
import { useCallback } from 'react';
import debounce from 'lodash/debounce';

type Args = {
  id: string;
  params?: object;
  page?: number;
  perPage?: number;
  sortableField?: string;
  sortableDirection?: string;
  queryKeyPrefix?: string;
};

const useReport = ({
  id,
  page,
  perPage,
  sortableField,
  sortableDirection,
  params = {},
  queryKeyPrefix = '',
}: Args) => {
  const { i18n } = useTranslation();
  const { version } = useUiVersion();
  const url = version === 'v2' ? `/api/reports/v2/${id}` : `/api/reports/${id}`;

  return useQuery({
    queryKey: [queryKeyPrefix, version, id, 'report_results', i18n.language],
    queryFn: () =>
      http.get<never, ReportResponse>(url, {
        params: {
          page,
          perPage,
          pageSize: perPage,
          order: sortableField,
          direction: sortableDirection,
          params: JSON.stringify(params),
        },
      }),
    refetchInterval: false,
  });
};

export const useReportTotalResults = ({ id, params = {} }: Args) => {
  const { i18n } = useTranslation();

  const query = useQuery({
    queryKey: [id, i18n, 'report_results_total_results'],
    queryFn: () =>
      http.get<never, { total: number }>(`/api/reports/${id}/total`, {
        params: {
          params: JSON.stringify(params),
        },
      }),
    refetchInterval: false,
  });

  const refetchDebounced = useCallback(debounce(query.refetch, 250), [
    query.refetch,
  ]);

  useShallowEqualEffect(() => {
    void refetchDebounced();
  }, [refetchDebounced, params]);

  return query;
};

export default useReport;
